// src/components/Loaders/PageFade.js
import React from "react";
import "./page-fade.css";
import tokaiLogo from "../../assets/tokai-images/logos/tokaiLogo.png";

const FADE_MS = 600;
const MIN_SHOW_MS = 700; // ✅ 0.9s minimum display time

export default function PageFade({
  imageSrc,
  targetSelector = ".page-header-image",
  cutoff = 6000,
  testCutoff = false,
  debug = false,
  logoSrc = tokaiLogo,
}) {
  const [progress, setProgress] = React.useState(0);
  const [hidden, setHidden] = React.useState(false);
  const [removed, setRemoved] = React.useState(false);

  React.useEffect(() => {
    const startTime = performance.now();
    let loaded = false;
    let p = 0;

    const img = new Image();

    const interval = setInterval(() => {
      if (!loaded) {
        p = Math.min(p + Math.random() * 12, 95);
        setProgress(p);
      }
    }, 150);

    const waitForElement = (selector, tmo = 2000) =>
      new Promise((resolve) => {
        const existing = document.querySelector(selector);
        if (existing) return resolve(existing);
        const start = performance.now();
        const loop = (ts) => {
          const el = document.querySelector(selector);
          if (el) return resolve(el);
          if (ts - start > tmo) return resolve(null);
          requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
      });

    const decodePromise = testCutoff
      ? new Promise(() => {})
      : typeof img.decode === "function"
        ? (img.src = imageSrc, img.decode().catch(() => {}))
        : new Promise((res) => {
            img.onload = res;
            img.onerror = res;
            img.src = imageSrc;
          });

    const finish = () => {
      if (loaded) return;
      loaded = true;

      clearInterval(interval);
      setProgress(100);

      const elapsed = performance.now() - startTime;
      const delay = Math.max(0, MIN_SHOW_MS - elapsed);

      setTimeout(() => {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            setHidden(true);
            setTimeout(() => setRemoved(true), FADE_MS + 50);
          })
        );
      }, delay);
    };

    const cutoffId = setTimeout(finish, cutoff);

    Promise.all([
      imageSrc || testCutoff ? decodePromise : Promise.resolve(),
      waitForElement(targetSelector, 2500),
    ])
      .then(finish)
      .catch(finish);

    return () => {
      clearInterval(interval);
      clearTimeout(cutoffId);
    };
  }, [imageSrc, targetSelector, cutoff, testCutoff, debug]);

  if (removed) return null;

  return (
    <div id="page-fade" className={hidden ? "fade-out" : ""} aria-hidden="true">
      <div className="logo-wrap" aria-label="Loading Tokai">
        <img
          src={logoSrc}
          alt="TOKAI"
          className="logo"
          decoding="async"
          height={92}
        />
      </div>

      <div className="progress-container progress-tokai">
        <div className="progress">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}
